import logoLight from "@/assets/logo-light.png";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Youtube, MessageCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const handleTestimoniosClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('testimonios');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <footer className="bg-tera-charcoal py-16 border-t border-primary-foreground/10">
      <div className="container">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img src={logoLight} alt="Tera Seguridad" className="h-12 w-auto mb-4" />
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-5">
              Soluciones integrales de seguridad electrónica para empresas desde 2011.
            </p>
            {/* Social Media CTAs */}
            <div className="flex flex-col gap-2.5">
              <a
                href="https://www.instagram.com/teraseguridad"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] rounded-lg hover:opacity-90 hover:scale-[1.02] transition-all duration-300 group"
              >
                <Instagram className="w-5 h-5 text-white" />
                <span className="text-sm font-medium text-white">Síguenos en Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/teraseguridad"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1558B2] rounded-lg hover:bg-[#124A9E] hover:scale-[1.02] transition-all duration-300 group"
              >
                <Facebook className="w-5 h-5 text-white" />
                <span className="text-sm font-medium text-white">Síguenos en Facebook</span>
              </a>
              <a
                href="https://www.youtube.com/@teraseguridad3276"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#CC0000] rounded-lg hover:bg-[#B30000] hover:scale-[1.02] transition-all duration-300 group"
              >
                <Youtube className="w-5 h-5 text-white" />
                <span className="text-sm font-medium text-white">Suscríbete en YouTube</span>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-primary-foreground mb-4">Servicios</h4>
            <ul className="space-y-2">
              <li>
                <a href="/videovigilancia-empresarial" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Videovigilancia
                </a>
              </li>
              <li>
                <a href="/control-de-acceso-empresarial" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Control de acceso
                </a>
              </li>
              <li>
                <a href="/control-de-asistencia-para-empresas" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Control de asistencia
                </a>
              </li>
              <li>
                <a href="/redes-empresarial" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Redes y conectividad
                </a>
              </li>
              <li>
                <a href="/alarmas-para-empresas" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Alarmas
                </a>
              </li>
              <li>
                <a href="/telefonia-empresarial" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Telefonía
                </a>
              </li>
              <li>
                <a href="/soporte-y-mantenimiento" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Soporte y mantenimiento
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-primary-foreground mb-4">Empresa</h4>
            <ul className="space-y-2">
              <li>
                <a href="/sobre-nosotros" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Sobre nosotros
                </a>
              </li>
              <li>
                <a href="/servicios" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Nuestros servicios
                </a>
              </li>
              <li>
                <a 
                  href="/#testimonios" 
                  onClick={handleTestimoniosClick}
                  className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors cursor-pointer"
                >
                  Testimonios
                </a>
              </li>
              <li>
                <a href="/contacto" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  Contacto
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-primary-foreground mb-4">Contacto</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/uGhc3TBLxbAqLRHa6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                >
                  Av. Lecuna, Edif. Sur 2, Piso 11<br />Caracas, Venezuela
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-[#25D366] flex-shrink-0" />
                <a 
                  href="https://wa.me/584264131822?text=Hola%2C%20me%20gustar%C3%ADa%20solicitar%20informaci%C3%B3n%20sobre%20sus%20servicios%20de%20seguridad." 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                >
                  +58 426-4131822
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-secondary flex-shrink-0" />
                <a href="tel:02129147414" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  0212-9147414
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-secondary flex-shrink-0" />
                <a href="mailto:ventas@teraseguridad.com" className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  ventas@teraseguridad.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-primary-foreground/70">
                  Lun - Vie: 8am - 4pm
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-primary-foreground/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-primary-foreground/50">
              © 2011 - {currentYear} Tera Seguridad C.A. Todos los derechos reservados.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-primary-foreground/50 hover:text-primary-foreground/70 transition-colors">
                Política de privacidad
              </a>
              <a href="#" className="text-sm text-primary-foreground/50 hover:text-primary-foreground/70 transition-colors">
                Términos de servicio
              </a>
            </div>
          </div>
          <p className="text-xs text-primary-foreground/60 text-center mt-6">
            WhatsApp, Instagram y Facebook son marcas registradas de Meta Platforms, Inc. YouTube es una marca registrada de Google LLC.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
